import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { active: Boolean, batch: Number, transitionUrl: String, locationUrl: String, lastAttempt: Number, denied: Boolean }
  static targets = ["message", "distributed", "remaining", "button"]

  connect() {
    this.disconnected = false
    this.busy = false
    this.key = `posting-location-${this.batchValue}`
    this.memoryState = { attempt: this.lastAttemptValue, denied: this.deniedValue }
    this.visibility = () => this.periodic()
    document.addEventListener("visibilitychange", this.visibility)
    this.timer = setInterval(() => this.periodic(), 60000)
    this.periodic()
  }
  disconnect() {
    clearInterval(this.timer)
    document.removeEventListener("visibilitychange", this.visibility)
    this.disconnected = true
  }
  state() {
    try { return JSON.parse(localStorage.getItem(this.key) || "{}") } catch (_) { return {} }
  }
  remember(state) {
    this.memoryState = state
    try { localStorage.setItem(this.key, JSON.stringify(state)) } catch (_) { /* only timing, no coordinates */ }
  }
  periodic() {
    if (!this.activeValue || this.busy || document.visibilityState !== "visible") return
    const saved = this.state()
    const state = (saved.attempt || 0) > (this.memoryState?.attempt || 0) ? saved : this.memoryState
    if (state.denied || Date.now() - (state.attempt || 0) < 30 * 60 * 1000) return
    this.perform("periodic")
  }
  start() { this.perform("start") }
  finish() { this.perform("finish") }
  manual() { this.perform("manual") }

  async perform(trigger) {
    if (this.busy) return
    if (trigger === "finish" && (!this.distributedTarget.checkValidity() || !this.remainingTarget.checkValidity())) {
      this.messageTarget.textContent = "配布枚数と残数を入力してください"
      return
    }
    this.busy = true
    this.buttonTargets.forEach(button => { button.disabled = true })
    this.messageTarget.textContent = "位置情報を確認しています…"
    this.remember({ ...this.state(), attempt: Date.now() })
    const location = await this.position()
    if (this.disconnected) return
    this.remember({ attempt: Date.now(), denied: location.status === "denied" })
    const transition = ["start", "finish"].includes(trigger)
    const body = { operation: trigger, trigger, location }
    if (trigger === "finish") {
      body.distributed_count = this.distributedTarget.value
      body.remaining_count = this.remainingTarget.value
    }
    try {
      const response = await fetch(transition ? this.transitionUrlValue : this.locationUrlValue, {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content }, body: JSON.stringify(body)
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || "保存できませんでした。ログイン状態・通信を確認してください")
      }
      if (trigger === "finish") this.activeValue = false
      window.location.reload()
    } catch (error) {
      this.messageTarget.textContent = error.message
      this.busy = false
      this.buttonTargets.forEach(button => { button.disabled = false })
    }
  }
  position() {
    const request_key = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
    if (!window.isSecureContext || !navigator.geolocation) return Promise.resolve({ request_key, status: "unsupported" })
    return new Promise(resolve => {
      let settled = false
      const done = value => { if (!settled) { settled = true; clearTimeout(timer); resolve({ request_key, ...value }) } }
      const timer = setTimeout(() => done({ status: "timeout" }), 20000)
      navigator.geolocation.getCurrentPosition(position => done({ status: "ok", latitude: position.coords.latitude,
        longitude: position.coords.longitude, accuracy: position.coords.accuracy, captured_at: new Date(position.timestamp).toISOString() }),
      error => done({ status: ({ 1: "denied", 2: "unavailable", 3: "timeout" })[error.code] || "unavailable" }),
      { enableHighAccuracy: false, maximumAge: 0, timeout: 15000 })
    })
  }
}
